import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, Phone, Mail, Building2, Tag, FileText, Loader2, CheckCircle,
  ChevronDown, MessageSquare, DollarSign, Calendar, Clock, Package,
  AlertTriangle, CreditCard, Receipt, Link2, Star, Megaphone, Bot,
  MessageCircle, Users, Send, Eye, PieChart, TrendingUp, Settings,
  Palette, UserPlus, GitBranch, Filter, BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// SHARED WIZARD COMPONENTS
// ═══════════════════════════════════════════════════════════════

const WizardModal = ({ isOpen, onClose, title, subtitle, icon: Icon, iconColor, children, footer, size = 'default' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    default: 'max-w-lg',
    large: 'max-w-2xl',
    xl: 'max-w-3xl'
  };
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 pt-20"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className={cn(
            "bg-[#12121a] rounded-2xl w-full border border-white/10 overflow-hidden max-h-[85vh] flex flex-col mt-4",
            sizeClasses[size]
          )}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", iconColor || "bg-primary/20")}>
                <Icon className={cn("w-5 h-5", iconColor ? iconColor.replace('bg-', 'text-').replace('/20', '-400') : "text-primary")} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{title}</h2>
                <p className="text-sm text-gray-400">{subtitle}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {children}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/10 flex justify-between items-center sticky bottom-0 bg-[#12121a]">
            {footer}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const FormField = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const TextInput = ({ icon: Icon, value, onChange, placeholder, type = 'text', ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />}
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50",
        Icon ? "pl-10 pr-4" : "px-4"
      )}
      {...props}
    />
  </div>
);

const TextArea = ({ icon: Icon, value, onChange, placeholder, rows = 3, maxLength, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-3 top-3 w-4 h-4 text-gray-500" />}
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={cn(
        "w-full py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 resize-none",
        Icon ? "pl-10 pr-4" : "px-4"
      )}
      {...props}
    />
    {maxLength && (
      <div className="absolute bottom-2 right-3 text-xs text-gray-500">
        {value?.length || 0}/{maxLength}
      </div>
    )}
  </div>
);

const SelectInput = ({ value, onChange, options, placeholder, ...props }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-primary/50"
    {...props}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(opt => (
      <option key={opt.value || opt} value={opt.value || opt}>
        {opt.label || opt}
      </option>
    ))}
  </select>
);

const AdvancedSection = ({ children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-t border-white/5 pt-4 mt-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
        Advanced Options
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ConfirmationBanner = ({ message, onConfirm, onCancel, loading }) => (
  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
    <div className="flex items-center gap-3 mb-3">
      <AlertTriangle className="w-5 h-5 text-amber-400" />
      <p className="text-sm text-amber-200">{message}</p>
    </div>
    <div className="flex gap-2">
      <Button size="sm" onClick={onConfirm} disabled={loading}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Confirm
      </Button>
      <Button size="sm" variant="outline" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
    </div>
  </div>
);

// Line Item Component for Proposals/Invoices
const LineItemInput = ({ items, onChange }) => {
  const addItem = () => {
    onChange([...items, { name: '', description: '', quantity: 1, price: 0 }]);
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange(newItems);
  };
  
  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };
  
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="p-3 bg-white/5 rounded-xl border border-white/10">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-5">
              <input
                type="text"
                value={item.name}
                onChange={e => updateItem(index, 'name', e.target.value)}
                placeholder="Item name"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
            </div>
            <div className="col-span-2">
              <input
                type="number"
                value={item.quantity}
                onChange={e => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                placeholder="Qty"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
            </div>
            <div className="col-span-3">
              <input
                type="number"
                value={item.price}
                onChange={e => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                placeholder="Price"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
            </div>
            <div className="col-span-2 flex items-center justify-end">
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="w-full py-2 border border-dashed border-white/20 rounded-xl text-sm text-gray-400 hover:bg-white/5"
      >
        + Add Line Item
      </button>
    </div>
  );
};

// Contact Selector Component (reusable)
const ContactSelector = ({ selectedContact, onSelect, label = "Select Contact" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  const searchContacts = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/search-contacts?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.contacts || []);
      }
    } catch (error) {
      toast.error('Error searching contacts');
    } finally {
      setSearching(false);
    }
  };
  
  return (
    <div className="space-y-3">
      <FormField label={label} required>
        <div className="flex gap-2">
          <TextInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, email, or phone..."
          />
          <Button variant="outline" onClick={searchContacts} disabled={searching}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
      </FormField>
      
      {searchResults.length > 0 && (
        <div className="border border-white/10 rounded-xl overflow-hidden max-h-32 overflow-y-auto">
          {searchResults.map(contact => (
            <button
              key={contact.id}
              type="button"
              onClick={() => {
                onSelect(contact);
                setSearchResults([]);
              }}
              className={cn(
                "w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left",
                selectedContact?.id === contact.id && "bg-primary/10"
              )}
            >
              <User className="w-4 h-4 text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  {contact.firstName} {contact.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {contact.email || contact.phone}
                </p>
              </div>
              {selectedContact?.id === contact.id && (
                <CheckCircle className="w-4 h-4 text-primary" />
              )}
            </button>
          ))}
        </div>
      )}
      
      {selectedContact && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
          <User className="w-4 h-4 text-primary" />
          <span className="text-sm text-primary">
            Selected: {selectedContact.firstName} {selectedContact.lastName}
          </span>
        </div>
      )}
    </div>
  );
};

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

// ═══════════════════════════════════════════════════════════════
// G) PAYMENTS / BILLING / PROPOSALS
// ═══════════════════════════════════════════════════════════════

// 1) Send Proposal Wizard
export function SendProposalWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [formData, setFormData] = useState({
    proposalTitle: initialData.proposalTitle || '',
    lineItems: initialData.lineItems || [{ name: '', description: '', quantity: 1, price: 0 }],
    tax: initialData.tax || 0,
    discount: initialData.discount || 0,
    expirationDate: initialData.expirationDate || '',
    sendImmediately: initialData.sendImmediately ?? true,
    messageBody: initialData.messageBody || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const taxAmount = subtotal * (formData.tax / 100);
    const discountAmount = formData.discount;
    return subtotal + taxAmount - discountAmount;
  };

  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }
    if (!formData.proposalTitle.trim()) {
      toast.error('Proposal title is required');
      return;
    }
    if (formData.lineItems.length === 0 || !formData.lineItems[0].name) {
      toast.error('At least one line item is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/proposal`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedContact.id,
          title: formData.proposalTitle,
          line_items: formData.lineItems,
          tax: formData.tax,
          discount: formData.discount,
          expiration_date: formData.expirationDate || undefined,
          send_immediately: formData.sendImmediately,
          message_body: formData.messageBody || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Proposal "${formData.proposalTitle}" ${formData.sendImmediately ? 'sent' : 'created'}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create proposal');
      }
    } catch (error) {
      toast.error('Error creating proposal: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Proposal"
      subtitle="Create and send a proposal to a contact"
      icon={FileText}
      iconColor="bg-green-500/20"
      size="large"
      footer={
        <>
          <div className="text-sm text-gray-400">
            Total: <span className="text-white font-semibold">${calculateTotal().toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {formData.sendImmediately ? 'Send Proposal' : 'Save Draft'}
            </Button>
          </div>
        </>
      }
    >
      <ContactSelector
        selectedContact={selectedContact}
        onSelect={setSelectedContact}
      />

      <FormField label="Proposal Title" required>
        <TextInput
          value={formData.proposalTitle}
          onChange={v => handleChange('proposalTitle', v)}
          placeholder="e.g., Website Development Proposal"
        />
      </FormField>

      <FormField label="Line Items" required>
        <LineItemInput
          items={formData.lineItems}
          onChange={items => handleChange('lineItems', items)}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Tax (%)">
          <TextInput
            type="number"
            value={formData.tax}
            onChange={v => handleChange('tax', parseFloat(v) || 0)}
            placeholder="0"
          />
        </FormField>
        <FormField label="Discount ($)">
          <TextInput
            type="number"
            value={formData.discount}
            onChange={v => handleChange('discount', parseFloat(v) || 0)}
            placeholder="0"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Expiration Date">
          <TextInput
            type="date"
            value={formData.expirationDate}
            onChange={v => handleChange('expirationDate', v)}
          />
        </FormField>
        <FormField label="Send Options">
          <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.sendImmediately}
              onChange={e => handleChange('sendImmediately', e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-white">Send immediately</span>
          </label>
        </FormField>
      </div>

      <FormField label="Custom Message (optional)">
        <TextArea
          value={formData.messageBody}
          onChange={v => handleChange('messageBody', v)}
          placeholder="Add a personal message to include with the proposal..."
          rows={3}
        />
      </FormField>
    </WizardModal>
  );
}

// 2) Create & Send Invoice Wizard
export function CreateInvoiceWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [formData, setFormData] = useState({
    invoiceNumber: initialData.invoiceNumber || '',
    autoGenerate: initialData.autoGenerate ?? true,
    lineItems: initialData.lineItems || [{ name: '', quantity: 1, price: 0 }],
    tax: initialData.tax || 0,
    discount: initialData.discount || 0,
    dueDate: initialData.dueDate || '',
    sendImmediately: initialData.sendImmediately ?? false,
    emailMessage: initialData.emailMessage || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const taxAmount = subtotal * (formData.tax / 100);
    return subtotal + taxAmount - formData.discount;
  };

  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }
    if (!formData.dueDate) {
      toast.error('Due date is required');
      return;
    }

    // If sending immediately, show confirmation first
    if (formData.sendImmediately && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/invoice`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedContact.id,
          invoice_number: formData.autoGenerate ? undefined : formData.invoiceNumber,
          auto_generate_number: formData.autoGenerate,
          line_items: formData.lineItems,
          tax: formData.tax,
          discount: formData.discount,
          due_date: formData.dueDate,
          send_immediately: formData.sendImmediately,
          email_message: formData.emailMessage || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Invoice ${formData.sendImmediately ? 'sent' : 'created'}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create invoice');
      }
    } catch (error) {
      toast.error('Error creating invoice: ' + error.message);
    } finally {
      setSubmitting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create & Send Invoice"
      subtitle="Create an invoice for a contact"
      icon={Receipt}
      iconColor="bg-green-500/20"
      size="large"
      footer={
        <>
          <div className="text-sm text-gray-400">
            Total: <span className="text-white font-semibold">${calculateTotal().toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              {formData.sendImmediately ? 'Send Invoice' : 'Create Invoice'}
            </Button>
          </div>
        </>
      }
    >
      {showConfirmation && (
        <ConfirmationBanner
          message={`Send invoice for $${calculateTotal().toFixed(2)} to ${selectedContact?.firstName} ${selectedContact?.lastName}?`}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirmation(false)}
          loading={submitting}
        />
      )}

      <ContactSelector
        selectedContact={selectedContact}
        onSelect={setSelectedContact}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Invoice Number">
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.autoGenerate}
                onChange={e => handleChange('autoGenerate', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-400">Auto-generate</span>
            </label>
            {!formData.autoGenerate && (
              <TextInput
                value={formData.invoiceNumber}
                onChange={v => handleChange('invoiceNumber', v)}
                placeholder="INV-001"
              />
            )}
          </div>
        </FormField>
        <FormField label="Due Date" required>
          <TextInput
            type="date"
            value={formData.dueDate}
            onChange={v => handleChange('dueDate', v)}
          />
        </FormField>
      </div>

      <FormField label="Line Items" required>
        <LineItemInput
          items={formData.lineItems}
          onChange={items => handleChange('lineItems', items)}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Tax (%)">
          <TextInput
            type="number"
            value={formData.tax}
            onChange={v => handleChange('tax', parseFloat(v) || 0)}
            placeholder="0"
          />
        </FormField>
        <FormField label="Discount ($)">
          <TextInput
            type="number"
            value={formData.discount}
            onChange={v => handleChange('discount', parseFloat(v) || 0)}
            placeholder="0"
          />
        </FormField>
      </div>

      <FormField label="Send Options">
        <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.sendImmediately}
            onChange={e => handleChange('sendImmediately', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <div>
            <span className="text-sm text-white">Send immediately</span>
            <p className="text-xs text-gray-500">Email invoice to contact after creation</p>
          </div>
        </label>
      </FormField>

      {formData.sendImmediately && (
        <FormField label="Email Message (optional)">
          <TextArea
            value={formData.emailMessage}
            onChange={v => handleChange('emailMessage', v)}
            placeholder="Add a custom message to include with the invoice email..."
            rows={2}
          />
        </FormField>
      )}
    </WizardModal>
  );
}

// 3) Create Payment Link Wizard
export function CreatePaymentLinkWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [formData, setFormData] = useState({
    linkName: initialData.linkName || '',
    amount: initialData.amount || '',
    paymentType: initialData.paymentType || 'one_time',
    billingFrequency: initialData.billingFrequency || 'monthly',
    trialPeriod: initialData.trialPeriod || '',
    productDescription: initialData.productDescription || '',
    sendLink: initialData.sendLink ?? false,
    sendMethod: initialData.sendMethod || 'email',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.linkName.trim()) {
      toast.error('Link name is required');
      return;
    }
    if (!formData.amount) {
      toast.error('Amount is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/payment-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          link_name: formData.linkName,
          amount: parseFloat(formData.amount),
          payment_type: formData.paymentType,
          billing_frequency: formData.paymentType === 'recurring' ? formData.billingFrequency : undefined,
          trial_period: formData.trialPeriod ? parseInt(formData.trialPeriod) : undefined,
          product_description: formData.productDescription || undefined,
          contact_id: selectedContact?.id,
          send_link: formData.sendLink,
          send_method: formData.sendLink ? formData.sendMethod : undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Payment link created!');
        if (result.payment_link) {
          navigator.clipboard.writeText(result.payment_link);
          toast.info('Link copied to clipboard!');
        }
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create payment link');
      }
    } catch (error) {
      toast.error('Error creating payment link: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Payment Link"
      subtitle="Generate a payment link to collect payments"
      icon={Link2}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Create Link
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Link Name" required>
        <TextInput
          value={formData.linkName}
          onChange={v => handleChange('linkName', v)}
          placeholder="e.g., Consultation Fee"
        />
      </FormField>

      <FormField label="Amount ($)" required>
        <TextInput
          icon={DollarSign}
          type="number"
          value={formData.amount}
          onChange={v => handleChange('amount', v)}
          placeholder="0.00"
        />
      </FormField>

      <FormField label="Payment Type" required>
        <div className="flex gap-3">
          <label className={cn(
            "flex-1 p-3 rounded-xl border cursor-pointer transition-colors",
            formData.paymentType === 'one_time' 
              ? "bg-primary/20 border-primary/50 text-primary" 
              : "bg-white/5 border-white/10 text-gray-400"
          )}>
            <input
              type="radio"
              name="paymentType"
              value="one_time"
              checked={formData.paymentType === 'one_time'}
              onChange={() => handleChange('paymentType', 'one_time')}
              className="sr-only"
            />
            <span className="text-sm">One-time</span>
          </label>
          <label className={cn(
            "flex-1 p-3 rounded-xl border cursor-pointer transition-colors",
            formData.paymentType === 'recurring' 
              ? "bg-primary/20 border-primary/50 text-primary" 
              : "bg-white/5 border-white/10 text-gray-400"
          )}>
            <input
              type="radio"
              name="paymentType"
              value="recurring"
              checked={formData.paymentType === 'recurring'}
              onChange={() => handleChange('paymentType', 'recurring')}
              className="sr-only"
            />
            <span className="text-sm">Recurring</span>
          </label>
        </div>
      </FormField>

      {formData.paymentType === 'recurring' && (
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Billing Frequency" required>
            <SelectInput
              value={formData.billingFrequency}
              onChange={v => handleChange('billingFrequency', v)}
              options={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'yearly', label: 'Yearly' },
              ]}
            />
          </FormField>
          <FormField label="Trial Period (days)">
            <TextInput
              type="number"
              value={formData.trialPeriod}
              onChange={v => handleChange('trialPeriod', v)}
              placeholder="0"
            />
          </FormField>
        </div>
      )}

      <FormField label="Product Description">
        <TextArea
          value={formData.productDescription}
          onChange={v => handleChange('productDescription', v)}
          placeholder="Describe what the payment is for..."
          rows={2}
        />
      </FormField>

      <AdvancedSection>
        <ContactSelector
          selectedContact={selectedContact}
          onSelect={setSelectedContact}
          label="Assign to Contact (optional)"
        />

        <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.sendLink}
            onChange={e => handleChange('sendLink', e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <div>
            <span className="text-sm text-white">Send link to contact</span>
            <p className="text-xs text-gray-500">Email or SMS the payment link</p>
          </div>
        </label>

        {formData.sendLink && (
          <FormField label="Send Method">
            <SelectInput
              value={formData.sendMethod}
              onChange={v => handleChange('sendMethod', v)}
              options={[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'SMS' },
                { value: 'both', label: 'Both' },
              ]}
            />
          </FormField>
        )}
      </AdvancedSection>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// H) REVIEWS / REPUTATION
// ═══════════════════════════════════════════════════════════════

// 4) Send Review Request Wizard
export function SendReviewRequestWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [formData, setFormData] = useState({
    reviewChannel: initialData.reviewChannel || 'google',
    customLink: initialData.customLink || '',
    customMessage: initialData.customMessage || '',
    delaySend: initialData.delaySend || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/review-request`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedContact.id,
          review_channel: formData.reviewChannel,
          custom_link: formData.reviewChannel === 'custom' ? formData.customLink : undefined,
          custom_message: formData.customMessage || undefined,
          delay_minutes: formData.delaySend ? parseInt(formData.delaySend) : undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Review request ${formData.delaySend ? 'scheduled' : 'sent'}!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send review request');
      }
    } catch (error) {
      toast.error('Error sending review request: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Review Request"
      subtitle="Request a review from a contact"
      icon={Star}
      iconColor="bg-amber-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
              Send Request
            </Button>
          </div>
        </>
      }
    >
      <ContactSelector
        selectedContact={selectedContact}
        onSelect={setSelectedContact}
      />

      <FormField label="Review Channel" required>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'google', label: 'Google' },
            { value: 'facebook', label: 'Facebook' },
            { value: 'custom', label: 'Custom Link' },
          ].map(opt => (
            <label
              key={opt.value}
              className={cn(
                "p-3 rounded-xl border cursor-pointer transition-colors text-center",
                formData.reviewChannel === opt.value
                  ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                  : "bg-white/5 border-white/10 text-gray-400"
              )}
            >
              <input
                type="radio"
                name="reviewChannel"
                value={opt.value}
                checked={formData.reviewChannel === opt.value}
                onChange={() => handleChange('reviewChannel', opt.value)}
                className="sr-only"
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </FormField>

      {formData.reviewChannel === 'custom' && (
        <FormField label="Custom Review Link" required>
          <TextInput
            icon={Link2}
            value={formData.customLink}
            onChange={v => handleChange('customLink', v)}
            placeholder="https://..."
          />
        </FormField>
      )}

      <FormField label="Custom Message (optional)">
        <TextArea
          value={formData.customMessage}
          onChange={v => handleChange('customMessage', v)}
          placeholder="Add a personalized message..."
          rows={3}
        />
      </FormField>

      <FormField label="Delay Send (optional)" hint="Leave empty to send immediately">
        <SelectInput
          value={formData.delaySend}
          onChange={v => handleChange('delaySend', v)}
          placeholder="Send immediately"
          options={[
            { value: '30', label: '30 minutes' },
            { value: '60', label: '1 hour' },
            { value: '120', label: '2 hours' },
            { value: '1440', label: '24 hours' },
          ]}
        />
      </FormField>
    </WizardModal>
  );
}

// 5) Create Review Campaign Wizard
export function CreateReviewCampaignWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    campaignName: initialData.campaignName || '',
    trigger: initialData.trigger || 'tag_added',
    triggerValue: initialData.triggerValue || '',
    delayHours: initialData.delayHours || '24',
    messageTemplate: initialData.messageTemplate || '',
    reminderEnabled: initialData.reminderEnabled ?? false,
    reminderDays: initialData.reminderDays || '3',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.campaignName.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    if (!formData.triggerValue && formData.trigger === 'tag_added') {
      toast.error('Please specify the trigger tag');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/review-campaign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          campaign_name: formData.campaignName,
          trigger: formData.trigger,
          trigger_value: formData.triggerValue || undefined,
          delay_hours: parseInt(formData.delayHours),
          message_template: formData.messageTemplate || undefined,
          reminder_enabled: formData.reminderEnabled,
          reminder_days: formData.reminderEnabled ? parseInt(formData.reminderDays) : undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Review campaign "${formData.campaignName}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create campaign');
      }
    } catch (error) {
      toast.error('Error creating campaign: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Review Campaign"
      subtitle="Set up automated review requests"
      icon={Megaphone}
      iconColor="bg-amber-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
              Create Campaign
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Campaign Name" required>
        <TextInput
          value={formData.campaignName}
          onChange={v => handleChange('campaignName', v)}
          placeholder="e.g., Post-Service Review Request"
        />
      </FormField>

      <FormField label="Trigger" required>
        <SelectInput
          value={formData.trigger}
          onChange={v => handleChange('trigger', v)}
          options={[
            { value: 'tag_added', label: 'Tag Added' },
            { value: 'opportunity_won', label: 'Opportunity Won' },
            { value: 'appointment_completed', label: 'Appointment Completed' },
          ]}
        />
      </FormField>

      {formData.trigger === 'tag_added' && (
        <FormField label="Trigger Tag" required>
          <TextInput
            icon={Tag}
            value={formData.triggerValue}
            onChange={v => handleChange('triggerValue', v)}
            placeholder="e.g., service-completed"
          />
        </FormField>
      )}

      <FormField label="Delay (hours)">
        <SelectInput
          value={formData.delayHours}
          onChange={v => handleChange('delayHours', v)}
          options={[
            { value: '1', label: '1 hour' },
            { value: '6', label: '6 hours' },
            { value: '24', label: '24 hours' },
            { value: '48', label: '48 hours' },
            { value: '72', label: '72 hours' },
          ]}
        />
      </FormField>

      <FormField label="Message Template">
        <TextArea
          value={formData.messageTemplate}
          onChange={v => handleChange('messageTemplate', v)}
          placeholder="Hi {{contact.first_name}}, we hope you enjoyed your experience..."
          rows={4}
        />
      </FormField>

      <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.reminderEnabled}
          onChange={e => handleChange('reminderEnabled', e.target.checked)}
          className="w-4 h-4 rounded"
        />
        <div className="flex-1">
          <span className="text-sm text-white">Enable reminder</span>
          <p className="text-xs text-gray-500">Send a follow-up if no review is left</p>
        </div>
        {formData.reminderEnabled && (
          <SelectInput
            value={formData.reminderDays}
            onChange={v => handleChange('reminderDays', v)}
            options={[
              { value: '3', label: '3 days' },
              { value: '5', label: '5 days' },
              { value: '7', label: '7 days' },
            ]}
          />
        )}
      </label>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// I) AI / CHATBOTS / AUTOMATIONS
// ═══════════════════════════════════════════════════════════════

// 6) Create Chat Widget Wizard
export function CreateChatWidgetWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    widgetName: initialData.widgetName || '',
    targetPageUrl: initialData.targetPageUrl || '',
    greetingMessage: initialData.greetingMessage || 'Hi! How can we help you today?',
    collectName: initialData.collectName ?? true,
    collectEmail: initialData.collectEmail ?? true,
    collectPhone: initialData.collectPhone ?? false,
    assignTo: initialData.assignTo || '',
    autoOpenDelay: initialData.autoOpenDelay || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.widgetName.trim()) {
      toast.error('Widget name is required');
      return;
    }
    if (!formData.greetingMessage.trim()) {
      toast.error('Greeting message is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/chat-widget`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          widget_name: formData.widgetName,
          target_page_url: formData.targetPageUrl || undefined,
          greeting_message: formData.greetingMessage,
          collect_name: formData.collectName,
          collect_email: formData.collectEmail,
          collect_phone: formData.collectPhone,
          assign_to: formData.assignTo || undefined,
          auto_open_delay: formData.autoOpenDelay ? parseInt(formData.autoOpenDelay) : undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Chat widget "${formData.widgetName}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create chat widget');
      }
    } catch (error) {
      toast.error('Error creating chat widget: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Chat Widget"
      subtitle="Set up a website chat widget"
      icon={MessageCircle}
      iconColor="bg-purple-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              Create Widget
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Widget Name" required>
        <TextInput
          value={formData.widgetName}
          onChange={v => handleChange('widgetName', v)}
          placeholder="e.g., Main Website Chat"
        />
      </FormField>

      <FormField label="Target Page URL (optional)" hint="Leave empty for all pages">
        <TextInput
          icon={Link2}
          value={formData.targetPageUrl}
          onChange={v => handleChange('targetPageUrl', v)}
          placeholder="https://yoursite.com/contact"
        />
      </FormField>

      <FormField label="Greeting Message" required>
        <TextArea
          value={formData.greetingMessage}
          onChange={v => handleChange('greetingMessage', v)}
          placeholder="Hi! How can we help you today?"
          rows={2}
        />
      </FormField>

      <FormField label="Collect Information">
        <div className="space-y-2">
          {[
            { field: 'collectName', label: 'Name' },
            { field: 'collectEmail', label: 'Email' },
            { field: 'collectPhone', label: 'Phone' },
          ].map(opt => (
            <label key={opt.field} className="flex items-center gap-3 p-2">
              <input
                type="checkbox"
                checked={formData[opt.field]}
                onChange={e => handleChange(opt.field, e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-300">{opt.label}</span>
            </label>
          ))}
        </div>
      </FormField>

      <AdvancedSection>
        <FormField label="Auto-Open Delay (seconds)">
          <SelectInput
            value={formData.autoOpenDelay}
            onChange={v => handleChange('autoOpenDelay', v)}
            placeholder="Don't auto-open"
            options={[
              { value: '5', label: '5 seconds' },
              { value: '10', label: '10 seconds' },
              { value: '30', label: '30 seconds' },
              { value: '60', label: '60 seconds' },
            ]}
          />
        </FormField>
      </AdvancedSection>
    </WizardModal>
  );
}

// 7) Create AI Chatbot Wizard
export function CreateAIChatbotWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    botName: initialData.botName || '',
    purpose: initialData.purpose || '',
    tone: initialData.tone || 'professional',
    knowledgeSource: initialData.knowledgeSource || 'website',
    websiteUrl: initialData.websiteUrl || '',
    customQA: initialData.customQA || '',
    escalationRule: initialData.escalationRule || 'notify_email',
    escalationEmail: initialData.escalationEmail || '',
    channels: initialData.channels || ['web_chat'],
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleChannel = (channel) => {
    const current = formData.channels;
    if (current.includes(channel)) {
      handleChange('channels', current.filter(c => c !== channel));
    } else {
      handleChange('channels', [...current, channel]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.botName.trim()) {
      toast.error('Bot name is required');
      return;
    }
    if (!formData.purpose.trim()) {
      toast.error('Purpose is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/ai-chatbot`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          bot_name: formData.botName,
          purpose: formData.purpose,
          tone: formData.tone,
          knowledge_source: formData.knowledgeSource,
          website_url: formData.knowledgeSource === 'website' ? formData.websiteUrl : undefined,
          custom_qa: formData.knowledgeSource === 'custom_qa' ? formData.customQA : undefined,
          escalation_rule: formData.escalationRule,
          escalation_email: formData.escalationEmail || undefined,
          channels: formData.channels,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`AI Chatbot "${formData.botName}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create AI chatbot');
      }
    } catch (error) {
      toast.error('Error creating AI chatbot: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create AI Chatbot"
      subtitle="Build an AI-powered chatbot"
      icon={Bot}
      iconColor="bg-purple-500/20"
      size="large"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
              Create Chatbot
            </Button>
          </div>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Bot Name" required>
          <TextInput
            value={formData.botName}
            onChange={v => handleChange('botName', v)}
            placeholder="e.g., Sales Assistant"
          />
        </FormField>
        <FormField label="Tone">
          <SelectInput
            value={formData.tone}
            onChange={v => handleChange('tone', v)}
            options={[
              { value: 'professional', label: 'Professional' },
              { value: 'friendly', label: 'Friendly' },
              { value: 'casual', label: 'Casual' },
              { value: 'formal', label: 'Formal' },
            ]}
          />
        </FormField>
      </div>

      <FormField label="Purpose" required>
        <TextArea
          value={formData.purpose}
          onChange={v => handleChange('purpose', v)}
          placeholder="Describe what this chatbot should do..."
          rows={2}
        />
      </FormField>

      <FormField label="Knowledge Source">
        <SelectInput
          value={formData.knowledgeSource}
          onChange={v => handleChange('knowledgeSource', v)}
          options={[
            { value: 'website', label: 'Website URL' },
            { value: 'custom_qa', label: 'Custom Q&A' },
            { value: 'uploaded_docs', label: 'Uploaded Documents' },
          ]}
        />
      </FormField>

      {formData.knowledgeSource === 'website' && (
        <FormField label="Website URL">
          <TextInput
            icon={Link2}
            value={formData.websiteUrl}
            onChange={v => handleChange('websiteUrl', v)}
            placeholder="https://yoursite.com"
          />
        </FormField>
      )}

      {formData.knowledgeSource === 'custom_qa' && (
        <FormField label="Custom Q&A" hint="Enter questions and answers">
          <TextArea
            value={formData.customQA}
            onChange={v => handleChange('customQA', v)}
            placeholder="Q: What are your hours?&#10;A: We're open 9am-5pm Mon-Fri..."
            rows={4}
          />
        </FormField>
      )}

      <FormField label="Channels">
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'web_chat', label: 'Web Chat' },
            { value: 'sms', label: 'SMS' },
            { value: 'facebook', label: 'Facebook' },
          ].map(opt => (
            <label
              key={opt.value}
              className={cn(
                "px-3 py-2 rounded-lg border cursor-pointer transition-colors",
                formData.channels.includes(opt.value)
                  ? "bg-purple-500/20 border-purple-500/50 text-purple-400"
                  : "bg-white/5 border-white/10 text-gray-400"
              )}
            >
              <input
                type="checkbox"
                checked={formData.channels.includes(opt.value)}
                onChange={() => toggleChannel(opt.value)}
                className="sr-only"
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </FormField>

      <FormField label="Escalation Rule">
        <SelectInput
          value={formData.escalationRule}
          onChange={v => handleChange('escalationRule', v)}
          options={[
            { value: 'notify_email', label: 'Notify via Email' },
            { value: 'notify_sms', label: 'Notify via SMS' },
            { value: 'assign_user', label: 'Assign to User' },
          ]}
        />
      </FormField>

      {formData.escalationRule === 'notify_email' && (
        <FormField label="Escalation Email">
          <TextInput
            icon={Mail}
            type="email"
            value={formData.escalationEmail}
            onChange={v => handleChange('escalationEmail', v)}
            placeholder="team@company.com"
          />
        </FormField>
      )}
    </WizardModal>
  );
}

// 8) Create AI Agent Wizard
export function CreateAIAgentWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    agentName: initialData.agentName || '',
    triggerEvent: initialData.triggerEvent || 'new_lead',
    primaryGoal: initialData.primaryGoal || '',
    allowedActions: initialData.allowedActions || ['send_sms'],
    escalationBehavior: initialData.escalationBehavior || 'notify',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAction = (action) => {
    const current = formData.allowedActions;
    if (current.includes(action)) {
      handleChange('allowedActions', current.filter(a => a !== action));
    } else {
      handleChange('allowedActions', [...current, action]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.agentName.trim()) {
      toast.error('Agent name is required');
      return;
    }
    if (!formData.primaryGoal.trim()) {
      toast.error('Primary goal is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/ai-agent`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          agent_name: formData.agentName,
          trigger_event: formData.triggerEvent,
          primary_goal: formData.primaryGoal,
          allowed_actions: formData.allowedActions,
          escalation_behavior: formData.escalationBehavior,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`AI Agent "${formData.agentName}" deployed!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create AI agent');
      }
    } catch (error) {
      toast.error('Error creating AI agent: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create AI Agent"
      subtitle="Deploy advanced AI automation"
      icon={Bot}
      iconColor="bg-purple-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
              Deploy Agent
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Agent Name" required>
        <TextInput
          value={formData.agentName}
          onChange={v => handleChange('agentName', v)}
          placeholder="e.g., Lead Qualifier Agent"
        />
      </FormField>

      <FormField label="Trigger Event" required>
        <SelectInput
          value={formData.triggerEvent}
          onChange={v => handleChange('triggerEvent', v)}
          options={[
            { value: 'new_lead', label: 'New Lead' },
            { value: 'missed_call', label: 'Missed Call' },
            { value: 'appointment_booked', label: 'Appointment Booked' },
            { value: 'tag_added', label: 'Tag Added' },
          ]}
        />
      </FormField>

      <FormField label="Primary Goal" required>
        <TextArea
          value={formData.primaryGoal}
          onChange={v => handleChange('primaryGoal', v)}
          placeholder="e.g., Qualify leads and book appointments..."
          rows={2}
        />
      </FormField>

      <FormField label="Allowed Actions">
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'send_sms', label: 'Send SMS' },
            { value: 'send_email', label: 'Send Email' },
            { value: 'add_tag', label: 'Add Tag' },
            { value: 'create_opportunity', label: 'Create Opportunity' },
          ].map(opt => (
            <label
              key={opt.value}
              className={cn(
                "p-3 rounded-lg border cursor-pointer transition-colors",
                formData.allowedActions.includes(opt.value)
                  ? "bg-purple-500/20 border-purple-500/50 text-purple-400"
                  : "bg-white/5 border-white/10 text-gray-400"
              )}
            >
              <input
                type="checkbox"
                checked={formData.allowedActions.includes(opt.value)}
                onChange={() => toggleAction(opt.value)}
                className="sr-only"
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </FormField>

      <FormField label="Escalation Behavior">
        <SelectInput
          value={formData.escalationBehavior}
          onChange={v => handleChange('escalationBehavior', v)}
          options={[
            { value: 'notify', label: 'Notify Team' },
            { value: 'assign', label: 'Assign to User' },
            { value: 'stop', label: 'Stop and Wait' },
          ]}
        />
      </FormField>
    </WizardModal>
  );
}

// Export all wizards
export {
  WizardModal,
  FormField,
  TextInput,
  TextArea,
  SelectInput,
  AdvancedSection,
  ContactSelector,
  LineItemInput,
  ConfirmationBanner,
  getAuthHeaders
};
